import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Shield, Smartphone, Bell, Users, RefreshCw, Trash2, X } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle, tdStyle, thStyle } from '../theme';
import DeviceApprovalModal from '../components/security/DeviceApprovalModal';

const tabBtn = (active) => ({
    padding: '9px 18px', borderRadius: '8px', border: 'none',
    backgroundColor: active ? theme.primary : '#e9ecef',
    color: active ? 'white' : '#555',
    fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer',
});

const STATUS_STYLES = {
    pending:         { bg:'#fef3c7', color:'#92400e', label:'Pending'  },
    'awaiting-code': { bg:'#dbeafe', color:'#1e40af', label:'Awaiting Code' },
    approved:        { bg:'#d1fae5', color:'#065f46', label:'Approved' },
    rejected:        { bg:'#fee2e2', color:'#991b1b', label:'Rejected' },
    revoked:         { bg:'#e5e7eb', color:'#374151', label:'Revoked'  },
};

const ALERT_COLORS = {
    info:     { bg:'#dbeafe', color:'#1e40af' },
    warning:  { bg:'#fef3c7', color:'#92400e' },
    critical: { bg:'#fee2e2', color:'#991b1b' },
};

export default function SecurityPage({ API_BASE }) {
    const [tab, setTab] = useState('devices');
    const [devices, setDevices] = useState([]);
    const [alerts,  setAlerts]  = useState([]);
    const [users,   setUsers]   = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [editingUser, setEditingUser] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const [d, a, u] = await Promise.all([
                axios.get(`${API_BASE}/security/devices`).catch(()=>({data:[]})),
                axios.get(`${API_BASE}/security/alerts`).catch(()=>({data:[]})),
                axios.get(`${API_BASE}/tenant-users`, { headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')||''}` } }).catch(()=>({data:[]})),
            ]);
            setDevices(d.data); setAlerts(a.data); setUsers(u.data);
        } catch {}
    }, [API_BASE]);

    useEffect(() => { refresh(); }, [refresh]);

    const fmt = d => d ? new Date(d).toLocaleString('en-IN', { dateStyle:'medium', timeStyle:'short' }) : '—';

    const revoke = async (id) => {
        if (!window.confirm('Revoke this device? It will be locked out immediately.')) return;
        await axios.put(`${API_BASE}/security/devices/${id}/revoke`);
        refresh();
    };
    const del = async (id) => {
        if (!window.confirm('Delete this device record? It will be treated as a brand-new device next time.')) return;
        await axios.delete(`${API_BASE}/security/devices/${id}`);
        refresh();
    };

    const markAlertSeen = async (id) => { await axios.put(`${API_BASE}/security/alerts/${id}/seen`); refresh(); };
    const markAllSeen   = async () => { await axios.put(`${API_BASE}/security/alerts/seen-all`); refresh(); };

    const saveContact = async () => {
        const u = editingUser;
        await axios.put(`${API_BASE}/security/users/${u._id}/contact`, {
            mobile: u.mobile || '', email: u.email || '', otpChannel: u.otpChannel || 'whatsapp',
        });
        setEditingUser(null); refresh();
    };

    return (
        <div>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'18px'}}>
                <div>
                    <h2 style={{margin:0, color:theme.navy, fontSize:'1.4rem', fontWeight:'700'}}>Security</h2>
                    <p style={{margin:'2px 0 0', color:theme.textMuted, fontSize:'0.85rem'}}>Devices, alerts, and staff contact for OTP-based password reset.</p>
                </div>
                <button onClick={refresh} style={{padding:'8px 14px', borderRadius:'8px', border:`1px solid ${theme.border}`, backgroundColor:'white', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', fontWeight:'600', fontSize:'0.82rem'}}>
                    <RefreshCw size={14}/> Refresh
                </button>
            </div>

            <div style={{display:'flex', gap:'8px', marginBottom:'18px'}}>
                <button onClick={()=>setTab('devices')} style={tabBtn(tab==='devices')}><Smartphone size={14} style={{verticalAlign:'middle', marginRight:'4px'}}/>Devices ({devices.length})</button>
                <button onClick={()=>setTab('alerts')}  style={tabBtn(tab==='alerts')}><Bell size={14} style={{verticalAlign:'middle', marginRight:'4px'}}/>Alerts ({alerts.filter(a=>!a.seen).length})</button>
                <button onClick={()=>setTab('staff')}   style={tabBtn(tab==='staff')}><Users size={14} style={{verticalAlign:'middle', marginRight:'4px'}}/>Staff Contact ({users.length})</button>
            </div>

            {tab === 'devices' && (
                <div style={cardStyle}>
                    {devices.length === 0 ? (
                        <div style={{textAlign:'center', padding:'40px', color:theme.textMuted}}>No devices yet</div>
                    ) : (
                        <table style={{width:'100%', borderCollapse:'collapse'}}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Device</th>
                                    <th style={thStyle}>Requester</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Last Seen</th>
                                    <th style={thStyle}>Approved</th>
                                    <th style={thStyle}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {devices.map(d => {
                                    const ss = STATUS_STYLES[d.status] || { bg:'#f1f5f9', color:'#475569', label:d.status };
                                    return (
                                        <tr key={d._id}>
                                            <td style={tdStyle}>
                                                <strong>{d.name || 'Unnamed'}</strong>
                                                {d.isAdminDevice && <span style={{marginLeft:'6px', padding:'1px 6px', borderRadius:'8px', backgroundColor:'#fef3c7', color:'#92400e', fontSize:'0.65rem', fontWeight:'700'}}>ADMIN</span>}
                                                <div style={{fontSize:'0.7rem', color:theme.textMuted, marginTop:'2px', fontFamily:'monospace'}}>{(d.fingerprint||'').slice(0,16)}…</div>
                                            </td>
                                            <td style={tdStyle}>
                                                {d.requesterName || '—'}
                                                <div style={{fontSize:'0.74rem', color:theme.textMuted}}>{d.requesterMobile}</div>
                                            </td>
                                            <td style={tdStyle}>
                                                <span style={{padding:'3px 10px', borderRadius:'12px', fontSize:'0.72rem', fontWeight:'700', backgroundColor:ss.bg, color:ss.color}}>{ss.label}</span>
                                            </td>
                                            <td style={tdStyle}>{fmt(d.lastSeenAt)}</td>
                                            <td style={tdStyle}>{fmt(d.approvedAt)}</td>
                                            <td style={tdStyle}>
                                                <div style={{display:'flex', gap:'4px', flexWrap:'wrap'}}>
                                                    {d.status === 'pending' && <button onClick={()=>setSelectedDevice(d)} style={{padding:'3px 10px', backgroundColor:theme.primary, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'700'}}>Review</button>}
                                                    {d.status === 'approved' && !d.isAdminDevice && <button onClick={()=>revoke(d._id)} style={{padding:'3px 10px', backgroundColor:theme.warning, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem', fontWeight:'700'}}>Revoke</button>}
                                                    {!d.isAdminDevice && <button onClick={()=>del(d._id)} title="Delete record" style={{padding:'3px 8px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.72rem'}}><Trash2 size={12}/></button>}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {tab === 'alerts' && (
                <div style={cardStyle}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px'}}>
                        <h3 style={{margin:0, color:theme.navy, fontSize:'1rem'}}>Recent Alerts</h3>
                        <button onClick={markAllSeen} style={{padding:'6px 12px', backgroundColor:'white', border:`1px solid ${theme.border}`, borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', fontWeight:'600'}}>Mark all seen</button>
                    </div>
                    {alerts.length === 0 ? (
                        <div style={{textAlign:'center', padding:'40px', color:theme.textMuted}}>No alerts yet</div>
                    ) : (
                        <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                            {alerts.map(a => {
                                const ac = ALERT_COLORS[a.severity] || ALERT_COLORS.info;
                                return (
                                    <div key={a._id} onClick={()=>!a.seen && markAlertSeen(a._id)}
                                        style={{padding:'12px 14px', borderRadius:'8px', backgroundColor: a.seen ? '#fafafa' : 'white', border:`1px solid ${a.seen ? theme.borderLight : ac.color}`, cursor: a.seen ? 'default' : 'pointer'}}>
                                        <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px'}}>
                                            <span style={{padding:'2px 10px', borderRadius:'10px', fontSize:'0.7rem', fontWeight:'700', backgroundColor:ac.bg, color:ac.color}}>{a.severity}</span>
                                            <strong style={{color:theme.navy, fontSize:'0.88rem'}}>{a.title}</strong>
                                            {!a.seen && <span style={{marginLeft:'auto', width:'8px', height:'8px', borderRadius:'50%', backgroundColor:theme.danger}}/>}
                                        </div>
                                        <div style={{fontSize:'0.82rem', color:theme.textMuted}}>{a.details}</div>
                                        <div style={{fontSize:'0.72rem', color:theme.textSubtle, marginTop:'4px'}}>
                                            {fmt(a.createdAt)}
                                            {a.deviceName && ` · Device: ${a.deviceName}`}
                                            {a.username && ` · User: ${a.username}`}
                                            {a.ip && ` · IP: ${a.ip}`}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {tab === 'staff' && (
                <div style={cardStyle}>
                    <h3 style={{margin:'0 0 6px', color:theme.navy, fontSize:'1rem'}}>Staff Contact (for OTP password reset)</h3>
                    <p style={{fontSize:'0.82rem', color:theme.textMuted, margin:'0 0 14px'}}>Each staff needs a mobile on file to use Forgot Password. They reset their own password via OTP — admin gets notified automatically.</p>
                    <table style={{width:'100%', borderCollapse:'collapse'}}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Name</th>
                                <th style={thStyle}>Username</th>
                                <th style={thStyle}>Mobile</th>
                                <th style={thStyle}>OTP Channel</th>
                                <th style={thStyle}>Email</th>
                                <th style={thStyle}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr key={u._id}>
                                    <td style={tdStyle}><strong>{u.fullName}</strong></td>
                                    <td style={tdStyle}>{u.username}</td>
                                    <td style={tdStyle}>{u.mobile || <span style={{color:theme.danger, fontWeight:'600'}}>missing</span>}</td>
                                    <td style={tdStyle}>{u.otpChannel || 'whatsapp'}</td>
                                    <td style={tdStyle}>{u.email || '—'}</td>
                                    <td style={tdStyle}>
                                        <button onClick={()=>setEditingUser({...u})} style={{padding:'4px 12px', backgroundColor:theme.primary, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>Edit</button>
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && <tr><td colSpan="6" style={{...tdStyle, textAlign:'center', color:theme.textMuted, padding:'30px'}}>No users — login as admin first</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedDevice && (
                <DeviceApprovalModal API_BASE={API_BASE} device={selectedDevice} onClose={()=>setSelectedDevice(null)} onDone={()=>{ setSelectedDevice(null); refresh(); }}/>
            )}

            {editingUser && (
                <div style={{position:'fixed', inset:0, backgroundColor:'rgba(15,23,42,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999}} onClick={()=>setEditingUser(null)}>
                    <div onClick={e=>e.stopPropagation()} style={{backgroundColor:'white', borderRadius:'14px', padding:'24px 28px', width:'400px', maxWidth:'95vw'}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px'}}>
                            <h3 style={{margin:0, color:theme.navy}}>Edit Contact — {editingUser.fullName}</h3>
                            <button onClick={()=>setEditingUser(null)} style={{background:'none', border:'none', cursor:'pointer'}}><X size={18}/></button>
                        </div>
                        <label style={labelStyle}>Mobile (with country code)</label>
                        <input value={editingUser.mobile||''} onChange={e=>setEditingUser({...editingUser, mobile:e.target.value})} style={inputStyle} placeholder="+9198XXXXXXXX"/>
                        <label style={labelStyle}>Email</label>
                        <input value={editingUser.email||''} onChange={e=>setEditingUser({...editingUser, email:e.target.value})} style={inputStyle}/>
                        <label style={labelStyle}>OTP Channel</label>
                        <select value={editingUser.otpChannel||'whatsapp'} onChange={e=>setEditingUser({...editingUser, otpChannel:e.target.value})} style={inputStyle}>
                            <option value="whatsapp">WhatsApp</option>
                            <option value="sms">SMS</option>
                            <option value="both">Both</option>
                        </select>
                        <button onClick={saveContact} style={{width:'100%', marginTop:'10px', padding:'10px', borderRadius:'8px', border:'none', backgroundColor:theme.primary, color:'white', fontWeight:'700', cursor:'pointer'}}>Save</button>
                    </div>
                </div>
            )}
        </div>
    );
}
